import { Input } from "@light-technology/components";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { api } from "../state/api";
import { type AppResponse } from "../state/api.types";
import { toSlug } from "../utilities/slug";
import { FormModal } from "./shared/FormModal";

type UpdateAppForm = {
  name: string;
};

type UpdateAppModalProps = {
  app: AppResponse;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const UpdateAppModal = (props: UpdateAppModalProps) => {
  const navigate = useNavigate();

  const [updateApp, updateAppResult] = api.useUpdateAppMutation();
  const { handleSubmit, reset, control } = useForm<UpdateAppForm>({
    defaultValues: { name: props.app.name },
  });
  const onSubmit = handleSubmit(({ name }) => {
    const slug = toSlug(name);
    updateApp({ uuid: props.app.uuid, name, slug }).then(() => {
      props.setIsOpen(false);
      navigate(
        location.pathname.replace(`/apps/${props.app.slug}`, `/apps/${slug}`),
      );
    });
  });

  useEffect(() => {
    if (props.isOpen) {
      reset({ name: props.app.name });
    }
  }, [props.isOpen, props.app.name, reset]);

  return (
    <FormModal
      isOpen={props.isOpen}
      onOpenChange={props.setIsOpen}
      textTitle={`What would you like to rename the ${props.app.name} app to?`}
      textCancel="Cancel"
      textSubmit="Rename app"
      isLoading={updateAppResult.isLoading}
      onCancel={() => {
        props.setIsOpen(false);
      }}
      onSubmit={onSubmit}
      data-ph-capture-attribute-action="update-app"
    >
      <Controller
        control={control}
        name="name"
        rules={{ required: "Name is required" }}
        render={({ field, fieldState }) => (
          <>
            <Input
              {...field}
              aria-label="App name"
              type="text"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
            <div className="text-sm text-tertiary">
              Slug preview: {toSlug(field.value)}
            </div>
          </>
        )}
      />
    </FormModal>
  );
};
